import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfileLabeledBullets,
  ProfilePublications,
  ProfileContactSection,
} from './StaffProfileTemplate';

const AkumtongImchenProfile = () => {
  const person = {
    id: 'akumtong-imchen',
    name: 'Akumtong Imchen',
    honorific: 'Mr',
    title: 'Assistant Researcher',
    affiliation: 'The Highland Institute, Kohima, Nagaland',
    image: 'https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/2u1aipg3_Akumtong.jpeg',
    email: 'akum@highlandinstitute.org',
    category: 'Research Staff',
    testid: 'akumtong-imchen-profile',
  };

  return (
    <StaffProfileTemplate person={person}>
      <ProfileSection title="Intellectual Biography">
        <ProfileParagraphs
          items={[
            'Akumtong Imchen is an Assistant Researcher at The Highland Institute (THI). As a social and cultural anthropologist, he specialises in the documentation of Indigenous knowledge systems and their intersections with urban and ecological transformation. His academic work is grounded in bottom-up ethnographic methodologies, drawing on primary data collection to explore the deep connections between cultural identity, ecological knowledge, and the natural world.',
            'At THI, Akumtong has played a key role in bridging academic research and public engagement. As coordinator of the Look Up exhibition, he helped translate complex research on ethno-astronomy, natural history, and ecological knowledge into accessible community narratives. His intellectual interests extend to the revitalisation of ancestral practices, particularly through the study of culture and tradition, Indigenous ecological knowledge, and the socio-cultural impacts of urbanisation in Highland Asia.',
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Research Foci & Areas of Work">
        <p className="text-stone-700 leading-relaxed mb-6">Akumtong Imchen's research focuses on:</p>
        <ProfileLabeledBullets
          items={[
            {
              label: 'Indigenous Ecological Knowledge',
              text: 'Documenting traditional interpretations of astronomy, botany, and zoology, with a particular focus on seasonal cycles and local biodiversity.',
            },
            {
              label: 'Urban Anthropology',
              text: 'Investigating the dynamics of urbanisation and tourism in Kohima, and exploring how traditional knowledge systems and social structures adapt to contemporary urban growth and sustainable development.',
            },
            {
              label: 'Cultural Heritage & Food Security',
              text: 'Researching the origins, continuity, and restoration of traditional Naga culinary systems, with a focus on food as a medium through which ancestral knowledge, ecological memory, and resilience are sustained amid changing climates.',
            },
            {
              label: 'Public Pedagogy',
              text: 'Developing curatorial and public-facing strategies (such as the Look Up exhibition), to return research findings to local communities through exhibitions and interactive sessions.',
            },
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Highland Institute Projects">
        <ProfileParagraphs
          items={[
            'At The Highland Institute, Akumtong Imchen has contributed as an assistant researcher to the URBALTOUR project, a collaboration between the French Institute of Pondicherry and The Highland Institute. The project examines the relationships between economy, sustainability, and tourism in hill stations, with Kohima town serving as one of its key case-study sites.',
            'He also served as coordinator of the Look Up exhibition, led by Edward Moon-Little, which focused on ethno-astronomy, botany, and zoology—including orchids, butterflies, gibbons, and birds—in relation to ecological knowledge and cultural heritage.',
            "Akumtong has also supported The Highland Institute's Film Club as assistant coordinator, helping to screen films and documentaries by tribal filmmakers on themes including gender, politics, culture and tradition, and ecological knowledge.",
            'He is currently involved in the Tasting Tomorrow project, initiated by Jonathon Keats, which explores Indigenous food ingredients and cuisines, and considers how climate change may affect future agricultural practices, food adaptation, and culinary resilience.',
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Selected Publications & Knowledge Outputs">
        <ProfilePublications
          items={[
            {
              citation:
                'Imchen, A., Lefebvre, B., Singh, N., & Yhome, R. (2025). Visitor Profiles, Spatial Patterns, and the Dynamics of Tourism in Kohima: An Executive Summary. Kohima, Nagaland: The Highland Institute. 15 pp.',
              url: 'https://doi.org/10.5281/zenodo.17526229',
            },
            {
              year: '2024',
              citation: (
                <>
                  'Tasting Tomorrow: Exploring climate-adapted cuisines in Nagaland' in <em>The Highlander Journal</em>, Vol. 3, Issue 2, pp. 73–75.
                </>
              ),
              url: 'https://doi.org/10.5281/zenodo.11001758',
            },
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Postgraduate Qualifications">
        <ProfileBullets items={['MSc in Anthropology']} />
      </ProfileSection>

      <ProfileContactSection
        email={person.email}
        role="Assistant Researcher, The Highland Institute"
        testidPrefix="akumtong"
        scholarlyProfiles={[
          { label: 'LinkedIn', url: 'https://www.linkedin.com/in/akumtong-imchen-9433742b9', testid: 'akumtong-linkedin-link' },
          { label: 'ORCID', url: 'https://orcid.org/0009-0002-6549-5481', testid: 'akumtong-orcid-link' },
          { label: 'Google Scholar', url: 'https://share.google/bzbwmqTGVrvtPOMPX', testid: 'akumtong-scholar-link' },
        ]}
      />
    </StaffProfileTemplate>
  );
};

export default AkumtongImchenProfile;
